import type { AppState, Field, Plan } from '../types';
import { isNatureOrZoneField } from './domain';
export interface FieldUndo {
  field: Field;
  scope: { kind: 'global' } | { kind: 'snapshot'; planId: string };
  plans: Pick<Plan, 'id' | 'selections' | 'snapshot'>[];
  activities: string[];
  attachments: string[];
}
export function addField(state: AppState, planId: string, field: Field): AppState {
  const plan = state.plans.find((p) => p.id === planId);
  if (!plan || plan.status === 'ready' || plan.estateId !== field.estateId)
    throw new Error('Marken kan ikke føjes til denne plan.');
  const existing = plan.snapshot?.fields ?? state.fields;
  if (existing.some((f) => f.id === field.id)) throw new Error('Marken findes allerede.');
  const isNatureOrZone = isNatureOrZoneField(field);
  const product = isNatureOrZone ? undefined : state.products.find((p) => p.active);
  return {
    ...state,
    fields: plan.snapshot ? state.fields : [...state.fields, structuredClone(field)],
    plans: state.plans.map((p) =>
      p.id === planId
        ? {
            ...p,
            selections: product ? { ...p.selections, [field.id]: product.id } : p.selections,
            snapshot: p.snapshot
              ? { ...p.snapshot, fields: [...p.snapshot.fields, structuredClone(field)] }
              : undefined,
            updatedAt: field.updatedAt,
          }
        : p,
    ),
  };
}
export function editField(
  state: AppState,
  planId: string,
  fieldId: string,
  patch: Partial<Field>,
): AppState {
  const plan = state.plans.find((p) => p.id === planId);
  if (!plan || plan.status === 'ready')
    throw new Error('Kopiér den færdige plan, før du ændrer marker.');
  const existing = (plan.snapshot?.fields ?? state.fields).find(
    (field) => field.id === fieldId && field.estateId === plan.estateId,
  );
  if (
    !existing ||
    (patch.id !== undefined && patch.id !== fieldId) ||
    (patch.estateId !== undefined && patch.estateId !== plan.estateId)
  )
    throw new Error('Marken kan ikke ændres i denne plan.');
  const change = (field: Field) =>
    field.id === fieldId ? { ...field, ...patch, updatedAt: new Date().toISOString() } : field;
  return {
    ...state,
    fields: plan.snapshot ? state.fields : state.fields.map(change),
    plans: state.plans.map((p) =>
      p.id === planId && p.snapshot
        ? {
            ...p,
            snapshot: { ...p.snapshot, fields: p.snapshot.fields.map(change) },
            updatedAt: new Date().toISOString(),
          }
        : p,
    ),
  };
}
export function removeField(
  state: AppState,
  planId: string,
  field: Field,
): { state: AppState; undo: FieldUndo } {
  const current = state.plans.find((p) => p.id === planId);
  if (!current || current.status === 'ready') throw new Error('En færdig plan kan ikke ændres.');
  const saved = (current.snapshot?.fields ?? state.fields).find(
    (f) => f.id === field.id && f.estateId === current.estateId,
  );
  if (!saved) throw new Error('Marken findes ikke i denne plan.');
  const local = !!current.snapshot;
  const affected = state.plans.filter(
    (p) =>
      p.id === planId ||
      (!local && !p.snapshot && p.estateId === saved.estateId && p.status !== 'ready'),
  );
  const undo: FieldUndo = {
    field: structuredClone(saved),
    scope: local ? { kind: 'snapshot', planId } : { kind: 'global' },
    plans: structuredClone(
      affected.map(({ id, selections, snapshot }) => ({ id, selections, snapshot })),
    ),
    activities: local
      ? []
      : state.activities.filter((a) => a.fieldId === field.id).map((a) => a.id),
    attachments: local
      ? []
      : state.attachments.filter((a) => a.fieldId === field.id).map((a) => a.id),
  };
  return {
    undo,
    state: {
      ...state,
      fields: local ? state.fields : state.fields.filter((f) => f.id !== field.id),
      plans: state.plans.map((p) => {
        if (!affected.some((a) => a.id === p.id)) return p;
        const selections = { ...p.selections };
        delete selections[field.id];
        return {
          ...p,
          selections,
          snapshot: p.snapshot
            ? { ...p.snapshot, fields: p.snapshot.fields.filter((f) => f.id !== field.id) }
            : undefined,
        };
      }),
      activities: local
        ? state.activities
        : state.activities.map((a) => (a.fieldId === field.id ? { ...a, fieldId: undefined } : a)),
      attachments: local
        ? state.attachments
        : state.attachments.map((a) => (a.fieldId === field.id ? { ...a, fieldId: undefined } : a)),
    },
  };
}
export function restoreField(state: AppState, undo: FieldUndo): AppState {
  const local = undo.scope.kind === 'snapshot';
  const scope = undo.scope;
  const target =
    scope.kind === 'snapshot' ? state.plans.find((p) => p.id === scope.planId) : undefined;
  if (
    (local &&
      (!target?.snapshot ||
        target.status === 'ready' ||
        target.snapshot.fields.some((f) => f.id === undo.field.id))) ||
    !state.estates.some((e) => e.id === undo.field.estateId) ||
    (!local && state.fields.some((f) => f.id === undo.field.id))
  )
    throw new Error('Marken kan ikke gendannes i den aktuelle arbejdsplads.');
  return {
    ...state,
    fields: local ? state.fields : [...state.fields, structuredClone(undo.field)],
    plans: state.plans.map((p) => {
      const previous = undo.plans.find((item) => item.id === p.id);
      if (!previous || p.status === 'ready') return p;
      const productId = previous.selections[undo.field.id];
      const selections = productId ? { ...p.selections, [undo.field.id]: productId } : p.selections;
      const savedField = previous.snapshot?.fields.find((f) => f.id === undo.field.id);
      const snapshot =
        p.snapshot && savedField && !p.snapshot.fields.some((f) => f.id === undo.field.id)
          ? { ...p.snapshot, fields: [...p.snapshot.fields, structuredClone(savedField)] }
          : p.snapshot;
      return { ...p, selections, snapshot };
    }),
    activities: state.activities.map((a) =>
      undo.activities.includes(a.id) && !a.fieldId ? { ...a, fieldId: undo.field.id } : a,
    ),
    attachments: state.attachments.map((a) =>
      undo.attachments.includes(a.id) && !a.fieldId ? { ...a, fieldId: undo.field.id } : a,
    ),
  };
}

export interface Point2D {
  x: number;
  y: number;
}

export interface LabelOffset {
  dx: number;
  dy: number;
}

const CANDIDATE_OFFSETS: LabelOffset[] = [
  { dx: 0, dy: 0 }, // Center
  { dx: 0, dy: -24 }, // Top
  { dx: 0, dy: 24 }, // Bottom
  { dx: -60, dy: 0 }, // Left
  { dx: 60, dy: 0 }, // Right
  { dx: -45, dy: -20 }, // Top-Left
  { dx: 45, dy: -20 }, // Top-Right
  { dx: -45, dy: 20 }, // Bottom-Left
  { dx: 45, dy: 20 }, // Bottom-Right
  { dx: 0, dy: -46 }, // Far Top
  { dx: 0, dy: 46 }, // Far Bottom
];

export interface BoundingBox {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export function computeNonOverlappingOffsets(
  points: Point2D[],
  boxWidth = 72,
  boxHeight = 22,
  obstacleBoxes: BoundingBox[] = [],
): LabelOffset[] {
  const halfW = boxWidth / 2;
  const halfH = boxHeight / 2;
  const placedBoxes: BoundingBox[] = [...obstacleBoxes];
  const results: LabelOffset[] = [];

  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    let bestCandidate = CANDIDATE_OFFSETS[0];
    let minOverlapArea = Infinity;

    for (const candidate of CANDIDATE_OFFSETS) {
      const cx = pt.x + candidate.dx;
      const cy = pt.y + candidate.dy;
      const minX = cx - halfW;
      const maxX = cx + halfW;
      const minY = cy - halfH;
      const maxY = cy + halfH;

      let totalOverlap = 0;
      for (const box of placedBoxes) {
        const overlapX = Math.max(0, Math.min(maxX, box.maxX) - Math.max(minX, box.minX));
        const overlapY = Math.max(0, Math.min(maxY, box.maxY) - Math.max(minY, box.minY));
        const area = overlapX * overlapY;
        if (area > 0) {
          totalOverlap += area;
        }
      }

      if (totalOverlap === 0) {
        bestCandidate = candidate;
        minOverlapArea = 0;
        break;
      }

      if (totalOverlap < minOverlapArea) {
        minOverlapArea = totalOverlap;
        bestCandidate = candidate;
      }
    }

    results.push(bestCandidate);
    placedBoxes.push({
      minX: pt.x + bestCandidate.dx - halfW,
      maxX: pt.x + bestCandidate.dx + halfW,
      minY: pt.y + bestCandidate.dy - halfH,
      maxY: pt.y + bestCandidate.dy + halfH,
    });
  }

  return results;
}

export const CANDIDATE_PIN_OFFSETS: LabelOffset[] = [
  { dx: 0, dy: 0 }, // Center
  { dx: 0, dy: -28 }, // Top
  { dx: 0, dy: 28 }, // Bottom
  { dx: -28, dy: 0 }, // Left
  { dx: 28, dy: 0 }, // Right
  { dx: -22, dy: -22 }, // Top-Left
  { dx: 22, dy: -22 }, // Top-Right
  { dx: -22, dy: 22 }, // Bottom-Left
  { dx: 22, dy: 22 }, // Bottom-Right
  { dx: 0, dy: -52 }, // Far Top
  { dx: 0, dy: 52 }, // Far Bottom
  { dx: -52, dy: 0 }, // Far Left
  { dx: 52, dy: 0 }, // Far Right
  { dx: -38, dy: -38 }, // Far Top-Left
  { dx: 38, dy: -38 }, // Far Top-Right
  { dx: -38, dy: 38 }, // Far Bottom-Left
  { dx: 38, dy: 38 }, // Far Bottom-Right
];

export function computeNonOverlappingPinOffsets(
  points: Point2D[],
  pinRadius = 14,
  obstacleBoxes: BoundingBox[] = [],
): LabelOffset[] {
  const half = pinRadius;
  const placedBoxes: BoundingBox[] = [...obstacleBoxes];
  const results: LabelOffset[] = [];

  for (let i = 0; i < points.length; i++) {
    const pt = points[i];
    let bestCandidate = CANDIDATE_PIN_OFFSETS[0];
    let minOverlapArea = Infinity;

    for (const candidate of CANDIDATE_PIN_OFFSETS) {
      const cx = pt.x + candidate.dx;
      const cy = pt.y + candidate.dy;
      const minX = cx - half;
      const maxX = cx + half;
      const minY = cy - half;
      const maxY = cy + half;

      let totalOverlap = 0;
      for (const box of placedBoxes) {
        const overlapX = Math.max(0, Math.min(maxX, box.maxX) - Math.max(minX, box.minX));
        const overlapY = Math.max(0, Math.min(maxY, box.maxY) - Math.max(minY, box.minY));
        const area = overlapX * overlapY;
        if (area > 0) {
          totalOverlap += area;
        }
      }

      if (totalOverlap === 0) {
        bestCandidate = candidate;
        minOverlapArea = 0;
        break;
      }

      if (totalOverlap < minOverlapArea) {
        minOverlapArea = totalOverlap;
        bestCandidate = candidate;
      }
    }

    results.push(bestCandidate);
    placedBoxes.push({
      minX: pt.x + bestCandidate.dx - half,
      maxX: pt.x + bestCandidate.dx + half,
      minY: pt.y + bestCandidate.dy - half,
      maxY: pt.y + bestCandidate.dy + half,
    });
  }

  return results;
}
